import { useCallback, useMemo, useRef, useState } from "react";
import { type ActivityEntry, isActivityTicket } from "../driver-activity-view-model";
import { toggled } from "./workspace-utils";

/**
 * The ticked tickets: single toggles, shift-click ranges over the tickets in view (`navigable`), and whole
 * drivers. Selection survives filtering, but not a reload that drops the tickets (`entries`).
 */
export type ActivitySelectionSession = {
  /** The day the selection belongs to; a new key starts from that day's saved selection. */
  key: string | undefined;
  initial: (key: string | undefined) => Iterable<string> | undefined;
};

export const useActivitySelection = (entries: ActivityEntry[], navigable: ActivityEntry[], session?: ActivitySelectionSession) => {
  const [state, setState] = useState<{ key: string | undefined; selected: ReadonlySet<string> }>(
    () => ({ key: session?.key, selected: new Set(session?.initial(session.key) ?? []) }),
  );
  // Another day: swap in its saved selection during this render, so the old day's never shows (or is saved) under it.
  let current = state;
  if (session && state.key !== session.key) {
    current = { key: session.key, selected: new Set(session.initial(session.key) ?? []) };
    setState(current);
  }
  const selected = current.selected;
  const setSelected = useCallback((update: ReadonlySet<string> | ((previous: ReadonlySet<string>) => ReadonlySet<string>)) =>
    setState((previous) => ({ key: previous.key, selected: typeof update === "function" ? update(previous.selected) : update })), []);
  // Where a shift-click range starts: the last ticket whose checkbox was used.
  const anchorKey = useRef<string | undefined>(undefined);

  const toggleSelected = useCallback((entry: ActivityEntry, options?: { range: boolean }) => {
    const tickets = navigable.filter(isActivityTicket);
    const from = tickets.findIndex((candidate) => candidate.key === anchorKey.current);
    const to = tickets.findIndex((candidate) => candidate.key === entry.key);
    if (options?.range && from >= 0 && to >= 0) {
      // Shift-click extends from the last ticket ticked to this one.
      setSelected((current) => new Set([...current, ...tickets.slice(Math.min(from, to), Math.max(from, to) + 1).map((candidate) => candidate.key)]));
    } else {
      setSelected((current) => toggled(current, entry.key));
    }
    anchorKey.current = entry.key;
  }, [navigable]);

  const selectEntries = useCallback((targets: ActivityEntry[], select: boolean) => setSelected((current) => {
    const next = new Set(current);
    for (const target of targets) {
      if (select) next.add(target.key);
      else next.delete(target.key);
    }
    return next;
  }), []);

  const selectedEntries = useMemo(() => entries.filter((entry) => selected.has(entry.key)), [entries, selected]);
  const clearSelection = useCallback(() => setSelected(new Set()), []);
  const selectOnly = useCallback((entry: ActivityEntry) => setSelected(new Set([entry.key])), []);

  return { selected, selectedEntries, toggleSelected, selectEntries, clearSelection, selectOnly };
};
